/**
 * Custom hook for managing Timeline component state and filtering logic.
 *
 * Handles:
 * - Item filtering by tags, search, type, and date range
 * - Sorting (newest to oldest)
 * - Selection state management
 * - Expansion state management
 * - Filter state management
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sortBodyPartDisplayStrings } from "../../../../../common/bodyPartOrdering";
import { DateISO } from "../../../utils/DateISO";
import {
  TimelineItemData,
  TimelineItemTypeConfig,
} from "../../TimelineItem/types";
import { getItemSearchMatches } from "../../TimelineItem/utils/searchHighlight";
import { TimelineFilters } from "../Timeline";
import { filterAndSortTimelineItems } from "../utils/filterAndSortTimelineItems";

/**
 * Timeline hook props configuration
 */
interface UseTimelineConfig<T extends string = string> {
  /**
   * Timeline items to manage
   */
  items: TimelineItemData<T>[];

  /**
   * Type configuration for getting type labels
   */
  typeConfig: Record<T, TimelineItemTypeConfig>;

  /**
   * Display mode
   */
  mode?: "display" | "edit";

  /**
   * Callback when selection changes
   */
  onItemSelect?: (selectedItems: string[]) => void;

  /**
   * Callback when an item is clicked.
   */
  onItemClick?: (item: TimelineItemData<T>, selected: boolean) => void;

  /**
   * Initial filter state
   */
  initialFilters?: Partial<TimelineFilters>;

  /**
   * Whether multiple selection is allowed
   */
  allowMultipleSelection?: boolean;

  /**
   * Force expand all timeline items
   */
  expandAll?: boolean;

  /**
   * Sort order for timeline items. Defaults to "desc" (newest to oldest).
   */
  sortOrder?: "asc" | "desc";

  /**
   * Claimed body parts to show in "In the claim" filter row
   */
  claimedBodyParts?: string[];

  /**
   * Priority map for types when sorting items with equal dates.
   * Lower number = higher priority (appears first).
   * Types not in map default to 999 (lowest priority).
   */
  typePriority?: Partial<Record<T, number>>;

  /** Sort suggest-edit → suggest-delete → other entries (edit mode). */
  groupBySuggestion?: boolean;
}

/**
 * Custom hook for Timeline state management
 */
export const useTimeline = <T extends string = string>({
  items,
  typeConfig,
  onItemSelect,
  onItemClick,
  initialFilters = {},
  allowMultipleSelection: _allowMultipleSelection = true,
  expandAll = false,
  mode = "display",
  sortOrder = "desc",
  claimedBodyParts = [],
  typePriority,
  groupBySuggestion = false,
}: UseTimelineConfig<T>) => {
  // Filter state
  const [filters, setFilters] = useState<TimelineFilters>({
    searchQuery: initialFilters.searchQuery || "",
    selectedTypes: initialFilters.selectedTypes || [],
    dateRange: {
      startDate: initialFilters.dateRange?.startDate || null,
      endDate: initialFilters.dateRange?.endDate || null,
    },
    showStarredOnly: initialFilters.showStarredOnly || false,
    showImportantOnly: initialFilters.showImportantOnly || false,
    selectedFiles: initialFilters.selectedFiles || [],
  });

  // Selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Last clicked index for range selection (SHIFT+click)
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

  // Expansion state
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Search match navigation state. Stored raw and clamped on read — see
  // `currentMatchIndex` below.
  const [rawMatchIndex, setRawMatchIndex] = useState<number>(0);

  // Keyboard navigation focus state (separate from selection)
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);

  // Track previous expandAll value to detect actual changes
  const prevExpandAllRef = useRef(expandAll);

  // Handle expandAll prop changes
  // Only react when expandAll actually changes, not when items array reference changes
  // (e.g., annotation updates shouldn't collapse manually expanded items)
  useEffect(() => {
    if (prevExpandAllRef.current !== expandAll) {
      // expandAll prop changed
      prevExpandAllRef.current = expandAll;
      if (expandAll) {
        // Expand all items
        setExpandedItems(new Set(items.map((item) => item.id)));
      } else {
        // Collapse all items
        setExpandedItems(new Set());
      }
    } else if (expandAll && items.length > 0) {
      // expandAll is true and items changed - expand any new items
      setExpandedItems((prev) => {
        const hasNewItems = items.some((item) => !prev.has(item.id));
        if (hasNewItems) {
          return new Set(items.map((item) => item.id));
        }
        return prev;
      });
    }
  }, [expandAll, items]);

  const clearSelections = useCallback(() => {
    setSelectedItems(new Set());
    setLastClickedIndex(null);
  }, []);

  // Collapse a specific item (for keyboard navigation)
  const collapseItem = useCallback((itemId: string) => {
    setExpandedItems((prev) => {
      const newExpanded = new Set(prev);
      newExpanded.delete(itemId);
      return newExpanded;
    });
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      newSelected.delete(itemId);
      return newSelected;
    });
  }, []);

  // Clear selection when mode changes (display <-> edit have separate selections)
  const prevModeRef = useRef(mode);
  useEffect(() => {
    // Only clear on actual mode changes, not on initial mount
    if (prevModeRef.current !== mode) {
      prevModeRef.current = mode;
      setSelectedItems(new Set());
      setExpandedItems(new Set());
      setLastClickedIndex(null);
      setFocusedItemId(null);
    }
  }, [mode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    // Clear anchor when filters change (to prevent stale range selection)
    setLastClickedIndex(null);
    // Clear keyboard focus when filters change (focused item may no longer be visible)
    setFocusedItemId(null);
  }, [filters]);

  // Clean up selection when items are deleted (remove non-existent IDs)
  useEffect(() => {
    setSelectedItems((prev) => {
      const itemIds = new Set(items.map((item) => item.id));
      const newSelected = new Set<string>();

      prev.forEach((id) => {
        if (itemIds.has(id)) {
          newSelected.add(id);
        }
      });

      // Only update if something changed
      if (newSelected.size !== prev.size) {
        // Notify parent of selection change
        onItemSelect?.(Array.from(newSelected));
        return newSelected;
      }

      return prev;
    });
  }, [items, onItemSelect]);

  // Split available tags into claimed and other based on claimedBodyParts
  const { claimedTags, otherTags } = useMemo(() => {
    // Collect all tags from timeline items
    const allTagSet = new Set<string>();
    items.forEach((item) => {
      item.tags?.forEach((tag) => allTagSet.add(tag));
    });

    const claimedSet = new Set(claimedBodyParts);

    // Claimed tags: always show ALL claimedBodyParts (even if not in items)
    // Sort anatomically (head to toe, right to left) - these are display strings
    const claimed = sortBodyPartDisplayStrings([...claimedBodyParts]);

    // Other tags: tags from items that are NOT in claimedBodyParts
    // Sort anatomically (head to toe, right to left) - these are display strings
    const other = sortBodyPartDisplayStrings(
      Array.from(allTagSet).filter((tag) => !claimedSet.has(tag)),
    );

    return {
      claimedTags: claimed,
      otherTags: other,
    };
  }, [items, claimedBodyParts]);

  // Type for dropdown option
  type TypeOption = {
    value: string;
    label: string;
    count?: number;
    nextCount?: number;
  };

  // Each type option carries how many entries it matches, across all items
  // (not the filtered view) so the numbers stay put while filters change.
  // `count` is the published state and `nextCount` what the next save yields:
  // in edit mode a pending delete, add, or type change makes them differ
  // (rendered "40 → 39"); in display mode the two always agree.
  const availableTypes = useMemo<TypeOption[]>(() => {
    const publishedByType = new Map<string, number>();
    const pendingByType = new Map<string, number>();
    const bump = (map: Map<string, number>, type: string) =>
      map.set(type, (map.get(type) ?? 0) + 1);
    items.forEach((item) => {
      if (!item.markedForCreation) {
        bump(publishedByType, item.originalType ?? item.type);
      }
      if (!item.markedForDeletion) {
        bump(pendingByType, item.type);
      }
    });
    const types = new Set([...publishedByType.keys(), ...pendingByType.keys()]);
    return Array.from(types, (type) => ({
      value: type,
      label: typeConfig[type as T].label || "",
      count: publishedByType.get(type) ?? 0,
      nextCount: pendingByType.get(type) ?? 0,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [items, typeConfig]);

  // Build list of PDF files present across items for the "Select file" filter.
  // Options are keyed by uploadedFileID so two PDFs sharing an S3 basename stay
  // separate, and labelled with the file's current name — item.pdfFileName is
  // the basename frozen at ingestion and goes stale on rename. Items with no
  // resolved file (V1 briefings) fall back to the name for both.
  const availableFiles = useMemo<TypeOption[]>(() => {
    const labelByValue = new Map<string, string>();
    items.forEach((item) => {
      const value = item.uploadedFileID ?? item.pdfFileName;
      // An item whose file resolves to no name at all would render a blank
      // option, so drop it the way the old name-keyed Set did.
      const label = item.displayFileName || item.pdfFileName;
      if (!value || !label) return;
      // First label wins: every item on a file resolves to the same name.
      if (!labelByValue.has(value)) {
        labelByValue.set(value, label);
      }
    });
    return Array.from(labelByValue, ([value, label]) => ({
      value,
      label,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  // Helper function to get type label (memoized for stability)
  const getTypeLabel = useCallback(
    (type: string): string => typeConfig[type as T]?.label ?? "",
    [typeConfig],
  );

  // Filter and sort items using extracted utility function
  const filteredItems = useMemo<TimelineItemData<T>[]>(() => {
    return filterAndSortTimelineItems(
      items,
      {
        selectedTags: [],
        searchQuery: filters.searchQuery,
        selectedTypes: filters.selectedTypes,
        dateRange: filters.dateRange,
        showStarredOnly: filters.showStarredOnly,
        showImportantOnly: filters.showImportantOnly,
        selectedFiles: filters.selectedFiles,
      },
      {
        sortOrder,
        typePriority,
        groupBySuggestion,
      },
      getTypeLabel,
    );
  }, [
    items,
    filters,
    sortOrder,
    typePriority,
    groupBySuggestion,
    getTypeLabel,
  ]);

  // Find all individual search matches with their positions.
  // Matches are numbered date → title → doctorName → content (visual
  // top-to-bottom) by getItemSearchMatches — the same numbering
  // TimelineItemContentV2 rebases `activeMatchIndex` against, so the "N of M"
  // counter and the rendered <mark>s always agree.
  const searchMatches = useMemo(() => {
    if (!filters.searchQuery.trim()) return [];

    const matches: Array<{
      item: TimelineItemData<T>;
      itemIndex: number;
      matchIndex: number;
      totalMatchesInItem: number;
    }> = [];

    filteredItems.forEach((item, itemIndex) => {
      const totalInItem = getItemSearchMatches(item, filters.searchQuery).total;

      for (let matchIndex = 0; matchIndex < totalInItem; matchIndex++) {
        matches.push({
          item,
          itemIndex,
          matchIndex,
          totalMatchesInItem: totalInItem,
        });
      }
    });

    return matches;
  }, [filteredItems, filters.searchQuery]);

  // Total count of matches
  const matchCount = searchMatches.length;

  // Clamp against the live match set. Only handleSearchChange resets the
  // index, so any OTHER filter change (type, date range, file, starred,
  // important) can shrink searchMatches and strand the index past the end.
  // currentMatchInfo then went undefined: the header rendered a nonsense
  // counter like "5001/12", no <mark> was active, and the scroll-to-match
  // effect no-op'd until the user hit next/prev. Derived rather than
  // corrected in an effect so there is no extra render.
  const currentMatchIndex =
    matchCount > 0 ? Math.min(rawMatchIndex, matchCount - 1) : 0;

  // Compute focused item index from filtered items
  const focusedItemIndex = useMemo(() => {
    if (!focusedItemId) return -1;
    return filteredItems.findIndex((item) => item.id === focusedItemId);
  }, [focusedItemId, filteredItems]);

  // Reset current match index when search query changes
  const handleSearchChange = useCallback((searchQuery: string) => {
    setFilters((prev) => ({
      ...prev,
      searchQuery,
    }));
    setRawMatchIndex(0);
  }, []);

  // Navigate to next match
  const goToNextMatch = useCallback(() => {
    if (matchCount === 0) return;
    const nextIndex = (currentMatchIndex + 1) % matchCount;
    // Auto-expand the item if it's collapsed
    const nextMatch = searchMatches[nextIndex];
    if (nextMatch) {
      setExpandedItems((expanded) => {
        if (!expanded.has(nextMatch.item.id)) {
          const newExpanded = new Set(expanded);
          newExpanded.add(nextMatch.item.id);
          return newExpanded;
        }
        return expanded;
      });
    }
    setRawMatchIndex(nextIndex);
  }, [matchCount, searchMatches, currentMatchIndex]);

  // Navigate to previous match
  const goToPreviousMatch = useCallback(() => {
    if (matchCount === 0) return;
    const prevIndex = (currentMatchIndex - 1 + matchCount) % matchCount;
    // Auto-expand the item if it's collapsed
    const prevMatch = searchMatches[prevIndex];
    if (prevMatch) {
      setExpandedItems((expanded) => {
        if (!expanded.has(prevMatch.item.id)) {
          const newExpanded = new Set(expanded);
          newExpanded.add(prevMatch.item.id);
          return newExpanded;
        }
        return expanded;
      });
    }
    setRawMatchIndex(prevIndex);
  }, [matchCount, searchMatches, currentMatchIndex]);

  // Get current match info for the active item
  const currentMatchInfo = searchMatches[currentMatchIndex];

  // Toggle expand/collapse for an item (display mode only)
  const handleToggleCollapse = useCallback((itemId: string) => {
    setExpandedItems((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }
      return newExpanded;
    });
  }, []);

  // Handle item click (selection + focus)
  const handleItemClick = useCallback(
    (item: TimelineItemData<T>, shiftKey = false) => {
      const isItemAlreadySelected = selectedItems.has(item.id);

      // Update keyboard focus to match clicked item (for keyboard navigation continuity)
      if (mode === "display") {
        setFocusedItemId(item.id);
      }

      // Notify parent component
      onItemClick?.(item, !isItemAlreadySelected);

      // Get current item index in filtered list
      const currentIndex = filteredItems.findIndex((i) => i.id === item.id);

      // Handle selection - DIFFERENT logic per mode
      setSelectedItems((prev) => {
        const newSelected = new Set(prev);

        if (mode === "edit") {
          // SHIFT+click range selection in edit mode
          if (shiftKey && lastClickedIndex !== null && currentIndex !== -1) {
            // Range selection: add all items between lastClickedIndex and currentIndex
            const start = Math.min(lastClickedIndex, currentIndex);
            const end = Math.max(lastClickedIndex, currentIndex);

            for (let i = start; i <= end; i++) {
              if (filteredItems[i]) {
                newSelected.add(filteredItems[i].id);
              }
            }

            // Don't update lastClickedIndex on shift+click (keep anchor)
          } else {
            // Normal toggle selection
            if (newSelected.has(item.id)) {
              newSelected.delete(item.id);
            } else {
              newSelected.add(item.id);
            }

            // Update anchor for next range selection (only on non-shift clicks)
            setLastClickedIndex(currentIndex);
          }
        } else {
          // Display mode: Single selection only
          if (newSelected.has(item.id)) {
            newSelected.clear();
          } else {
            newSelected.clear();
            newSelected.add(item.id);
          }
        }

        // Notify parent component
        onItemSelect?.(Array.from(newSelected));

        return newSelected;
      });
    },
    [
      onItemSelect,
      onItemClick,
      mode,
      lastClickedIndex,
      filteredItems,
      selectedItems,
    ],
  );

  // Filter handlers

  const handleTypeFilter = useCallback((selectedTypes: string[]) => {
    setFilters((prev) => ({
      ...prev,
      selectedTypes,
    }));
  }, []);

  const handleDateRangeChange = useCallback(
    (startDate: DateISO | null, endDate: DateISO | null) => {
      setFilters((prev) => ({
        ...prev,
        dateRange: {
          startDate,
          endDate,
        },
      }));
    },
    [],
  );

  const handleStarredFilterChange = useCallback((showStarredOnly: boolean) => {
    setFilters((prev) => ({
      ...prev,
      showStarredOnly,
    }));
  }, []);

  const handleImportantFilterChange = useCallback(
    (showImportantOnly: boolean) => {
      setFilters((prev) => ({
        ...prev,
        showImportantOnly,
      }));
    },
    [],
  );

  const handleFileFilter = useCallback((selectedFiles: string[]) => {
    setFilters((prev) => ({
      ...prev,
      selectedFiles,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      searchQuery: "",
      selectedTypes: [],
      dateRange: {
        startDate: null,
        endDate: null,
      },
      showStarredOnly: false,
      showImportantOnly: false,
      selectedFiles: [],
    });
  }, []);

  const selectItem = useCallback(
    (itemId: string) => {
      setSelectedItems((prev) => {
        const newSelected = new Set(prev);
        if (mode === "display") {
          newSelected.clear();
        }
        newSelected.add(itemId);
        return newSelected;
      });

      if (mode === "display") {
        setExpandedItems((prev) => {
          const newExpanded = new Set(prev);
          newExpanded.add(itemId);
          return newExpanded;
        });

        // Also set keyboard focus for navigation continuity
        setFocusedItemId(itemId);
      }
    },
    [mode],
  );

  return {
    filteredItems,
    selectedItems,
    expandedItems,
    filters,
    claimedTags,
    otherTags,
    availableTags: [...claimedTags, ...otherTags], // Keep for backward compat
    availableTypes,
    availableFiles,
    handleItemClick,
    handleToggleCollapse,
    handleSearchChange,
    handleTypeFilter,
    handleDateRangeChange,
    handleStarredFilterChange,
    handleImportantFilterChange,
    handleFileFilter,
    clearFilters,
    clearSelections,
    collapseItem,
    selectItem,

    // Search match navigation
    searchMatches,
    matchCount,
    currentMatchIndex,
    currentMatchInfo,
    goToNextMatch,
    goToPreviousMatch,

    // Keyboard navigation
    focusedItemId,
    focusedItemIndex,
    setFocusedItemId,
  };
};
